<template>
  <div class="settings-view is-narrow">
    <PageHeader title="Profile" subtitle="Your personal account details." />
    <div v-if="message" class="workspace-alert">{{ message }}</div>
    <AsyncState :loading="!profile">
      <div class="settings-form-card">
        <div class="profile-identity">
          <UserAvatar :name="profile?.name" size="large" aria-hidden="true" />
          <div class="profile-identity-text">
            <div class="profile-identity-name">{{ profile?.name }}</div>
            <div class="profile-identity-email">{{ profile?.email }}</div>
          </div>
        </div>

        <div class="setting-row">
          <div class="setting-row-head">
            <div class="setting-row-label">Display name</div>
            <div class="setting-row-desc">Shown to other members across the workspace.</div>
          </div>
          <div class="setting-control">
            <input v-model="name" class="setting-input" maxlength="100" @keyup.enter="saveName" />
            <button type="button" class="btn btn-primary" :disabled="!nameDirty" @click="saveName">Save name</button>
          </div>
        </div>

        <div class="setting-row">
          <div class="setting-row-head">
            <div class="setting-row-label">Email address</div>
            <div class="setting-row-desc">Changing your email requires confirmation via a link.</div>
          </div>
          <div class="setting-control">
            <label class="control-field">
              <span class="control-field-label">Current</span>
              <input class="setting-input" type="email" :value="profile?.email" readonly />
            </label>
            <label class="control-field">
              <span class="control-field-label">New email</span>
              <input v-model="newEmail" class="setting-input" type="email" placeholder="new@email.com" autocomplete="email" />
            </label>
            <button type="button" class="btn" :disabled="!newEmail.trim()" @click="requestEmailChange">Send confirmation</button>
          </div>
        </div>

        <div class="setting-row">
          <div class="setting-row-head">
            <div class="setting-row-label">Password</div>
            <div class="setting-row-desc">Use 5 to 50 characters.</div>
          </div>
          <div class="setting-control">
            <label class="control-field">
              <span class="control-field-label">New password</span>
              <input v-model="password" class="setting-input" type="password" autocomplete="new-password" />
            </label>
            <label class="control-field">
              <span class="control-field-label">Confirm</span>
              <input v-model="passwordConfirmation" class="setting-input" type="password" autocomplete="new-password" />
            </label>
            <button type="button" class="btn btn-primary" :disabled="!password" @click="savePassword">Update password</button>
          </div>
        </div>
      </div>
      <PersonalAccessTokensSettings />
    </AsyncState>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { CommandsService } from '../api/generated';
import AsyncState from '../components/settings/AsyncState.vue';
import PageHeader from '../components/settings/PageHeader.vue';
import PersonalAccessTokensSettings from '../components/settings/PersonalAccessTokensSettings.vue';
import UserAvatar from '../components/UserAvatar.vue';
import { getErrorMessage, useSessionStore } from '../stores/session';
import {
  validateRequiredEmail,
  validateRequiredPassword,
  validateRequiredUserName
} from '../validation/userValidation';

const session = useSessionStore();
const profile = computed(() => session.profile);
const name = ref('');
const newEmail = ref('');
const password = ref('');
const passwordConfirmation = ref('');
const message = ref<string | null>(null);

const nameDirty = computed(() => {
  const trimmed = name.value.trim();
  return profile.value != null && trimmed !== profile.value.name;
});

onMounted(load);

async function load(): Promise<void> {
  try {
    if (profile.value == null) {
      throw new Error('Profile bootstrap payload was missing.');
    }

    syncName();
    message.value = null;
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to load settings.');
  }
}

function syncName(): void {
  name.value = profile.value?.name ?? '';
}

async function saveName(): Promise<void> {
  if (!profile.value || !nameDirty.value) {
    return;
  }

  const validationMessage = validateRequiredUserName(name.value);
  if (validationMessage != null) {
    message.value = validationMessage;
    return;
  }

  try {
    session.profile = await CommandsService.commandUpdateCurrentUserProfileCommand({
      requestBody: { name: name.value.trim(), password: null }
    });
    message.value = 'Name updated.';
    syncName();
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to update name.');
  }
}

async function savePassword(): Promise<void> {
  if (!profile.value || !password.value) {
    return;
  }

  if (password.value !== passwordConfirmation.value) {
    message.value = 'New password and confirmation do not match.';
    return;
  }

  const validationMessage = validateRequiredPassword(password.value);
  if (validationMessage != null) {
    message.value = validationMessage;
    return;
  }

  try {
    session.profile = await CommandsService.commandUpdateCurrentUserProfileCommand({
      requestBody: { name: profile.value.name, password: password.value }
    });
    password.value = '';
    passwordConfirmation.value = '';
    message.value = 'Password updated.';
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to update password.');
  }
}

async function requestEmailChange(): Promise<void> {
  const validationMessage = validateRequiredEmail(newEmail.value);
  if (validationMessage != null) {
    message.value = validationMessage;
    return;
  }

  try {
    await CommandsService.commandRequestEmailChangeCommand({ requestBody: { email: newEmail.value.trim() } });
    newEmail.value = '';
    message.value = 'Email confirmation sent.';
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to request email change.');
  }
}
</script>
